/**
 * For normal action click that only required to track symbol properties,
 * Use  @function actionClick pageAnalyisService instead
 *
 */

import mixpanel from '@aqx-core/common/mixpanel';

import { mapPeriodToText } from '../../trade/analysis/equity-chart/utils/period';
import { eventNameHelper, placeOrderHelper, userHelper } from '../helpers';
import chartTrackingHelper from '../helpers/ChartTrackingHelper';
import { checkIsModifyTracking } from '../helpers/ChartTrackingHelper/utils';
import { EMixpanelEventNames, EMixpanelProperties } from '../types';
import {
  IMixpanelChartSettingItemsClickedProps,
  IMixpanelChartTrackingProps,
  IMixpanelChartTypeSelectProps,
  IMixpanelDrawToolSelectProps,
  IMixpanelIndicatorSelectProps,
  IMixpanelTimeframeSelectProps
} from './type';

class ChartAnalysisService {
  public indicatorSelect = ({ symbol, indicator }: IMixpanelIndicatorSelectProps) => {
    const userAccountProperties = userHelper.createUserAccountProperties();
    const fullChartProperties = placeOrderHelper.createFullChartProperties();
    const tradePreferenceProperties = placeOrderHelper.createTradePreferenceProperties();
    mixpanel.track(EMixpanelEventNames.INDICATORS_SELECTED, {
      [EMixpanelProperties.SYMBOL]: symbol ?? '-',
      [EMixpanelProperties.INDICATORS_SELECTED]: indicator ?? '-',
      ...userAccountProperties,
      ...fullChartProperties,
      ...tradePreferenceProperties
    });
  };

  public chartTypeSelected = ({ symbol, chartType }: IMixpanelChartTypeSelectProps) => {
    const userAccountProperties = userHelper.createUserAccountProperties();
    const fullChartProperties = placeOrderHelper.createFullChartProperties();
    const tradePreferenceProperties = placeOrderHelper.createTradePreferenceProperties();
    mixpanel.track(EMixpanelEventNames.CHART_TYPE_SELECTED, {
      [EMixpanelProperties.SYMBOL]: symbol ?? '-',
      [EMixpanelProperties.CHART_TYPE_SELECTED]: chartType ?? '-',
      ...userAccountProperties,
      ...fullChartProperties,
      ...tradePreferenceProperties
    });
  };

  public timeframeSelected = ({ symbol, timeframe }: IMixpanelTimeframeSelectProps) => {
    const userAccountProperties = userHelper.createUserAccountProperties();
    const fullChartProperties = placeOrderHelper.createFullChartProperties();
    const tradePreferenceProperties = placeOrderHelper.createTradePreferenceProperties();
    mixpanel.track(EMixpanelEventNames.TIMEFRAME_SELECTED, {
      [EMixpanelProperties.SYMBOL]: symbol ?? '-',
      [EMixpanelProperties.TIMEFRAME_SELECTED]: mapPeriodToText(timeframe) ?? '-',
      ...userAccountProperties,
      ...fullChartProperties,
      ...tradePreferenceProperties
    });
  };

  public chartSettingItemsClicked = ({ action, symbol, source, isEnabled }: IMixpanelChartSettingItemsClickedProps) => {
    const userAccountProperties = userHelper.createUserAccountProperties();
    const fullChartProperties = placeOrderHelper.createFullChartProperties();
    const tradePreferenceProperties = placeOrderHelper.createTradePreferenceProperties();
    const eventName = eventNameHelper.mapChartSettingItemsClickedEventName(action);
    mixpanel.track(eventName, {
      [EMixpanelProperties.SYMBOL]: symbol ?? '-',
      [EMixpanelProperties.IS_ENABLED]: isEnabled ?? undefined,
      [EMixpanelProperties.SETTING_SOURCE]: source ?? '-',
      ...userAccountProperties,
      ...fullChartProperties,
      ...tradePreferenceProperties
    });
  };

  public chartDrawToolSelected = ({ symbol, drawingTool }: IMixpanelDrawToolSelectProps) => {
    const userAccountProperties = userHelper.createUserAccountProperties();
    const fullChartProperties = placeOrderHelper.createFullChartProperties();
    const tradePreferenceProperties = placeOrderHelper.createTradePreferenceProperties();
    mixpanel.track(EMixpanelEventNames.DRAWING_TOOL_SELECTED, {
      [EMixpanelProperties.SYMBOL]: symbol ?? '-',
      [EMixpanelProperties.DRAWING_TOOL_SELECTED]: drawingTool ?? '-',
      ...userAccountProperties,
      ...fullChartProperties,
      ...tradePreferenceProperties
    });
  };

  public chartDrawingCompleted = ({ symbol, drawingTool }: IMixpanelDrawToolSelectProps) => {
    const userAccountProperties = userHelper.createUserAccountProperties();
    const fullChartProperties = placeOrderHelper.createFullChartProperties();
    const tradePreferenceProperties = placeOrderHelper.createTradePreferenceProperties();
    mixpanel.track(eventNameHelper.mapCompleteDrawingEventName(drawingTool), {
      [EMixpanelProperties.SYMBOL]: symbol ?? '-',
      ...userAccountProperties,
      ...fullChartProperties,
      ...tradePreferenceProperties
    });
  };

  public chartDeleteDrawingClicked = ({ symbol, drawingTool }: IMixpanelDrawToolSelectProps) => {
    const userAccountProperties = userHelper.createUserAccountProperties();
    const fullChartProperties = placeOrderHelper.createFullChartProperties();
    const tradePreferenceProperties = placeOrderHelper.createTradePreferenceProperties();
    mixpanel.track(eventNameHelper.mapDeleteDrawingEventName(drawingTool), {
      [EMixpanelProperties.SYMBOL]: symbol ?? '-',
      ...userAccountProperties,
      ...fullChartProperties,
      ...tradePreferenceProperties
    });
  };

  public chartTrackedOrModifiedOrRemoveClicked = (props: IMixpanelChartTrackingProps) => {
    const { SLTPType, isRemoveTracking } = props;

    if (isRemoveTracking) {
      return this.chartRemovedSetClicked(props);
    }

    const userAccountProperties = userHelper.createUserAccountProperties();
    const fullChartProperties = placeOrderHelper.createFullChartProperties();
    const tradePreferenceProperties = placeOrderHelper.createTradePreferenceProperties();
    const chartTrackingProperties = chartTrackingHelper.createChartTrackingProperties(props);

    const isModifyTracking = checkIsModifyTracking(SLTPType);

    return mixpanel.track(
      isModifyTracking
        ? EMixpanelEventNames.CHART_TRACKED_MODIFY_CLICKED
        : EMixpanelEventNames.CHART_TRACKED_SET_CLICKED,
      {
        ...chartTrackingProperties,
        ...userAccountProperties,
        ...fullChartProperties,
        ...tradePreferenceProperties
      }
    );
  };

  public chartRemovedSetClicked = (props: IMixpanelChartTrackingProps) => {
    const userAccountProperties = userHelper.createUserAccountProperties();
    const fullChartProperties = placeOrderHelper.createFullChartProperties();
    const tradePreferenceProperties = placeOrderHelper.createTradePreferenceProperties();
    const chartTrackingProperties = chartTrackingHelper.createChartTrackingProperties(props);
    mixpanel.track(EMixpanelEventNames.CHART_TRACKED_REMOVED_CLICKED, {
      ...chartTrackingProperties,
      ...userAccountProperties,
      ...fullChartProperties,
      ...tradePreferenceProperties
    });
  };
}

const chartAnalysisService = new ChartAnalysisService();

export default chartAnalysisService;
